import type { EditorAsset } from '../core/models/editor-asset.model';

type MediaAssetKind = 'video' | 'image' | 'audio';
type SourceResolver = (url: string, kind: MediaAssetKind) => Promise<string>;
type SourceFetcher = (input: string) => Promise<Pick<Response, 'ok' | 'status' | 'blob'>>;

/**
 * Prepare one Media-tab asset for worker decoding.
 *
 * Existing Blobs are returned untouched. Session blob/data URLs are materialized
 * on the main thread, and durable storage URLs may first resolve through the
 * authenticated preview cache. The worker therefore receives BlobSource bytes
 * instead of making another provider/storage request through UrlSource.
 */
export async function prepareEditorAssetForWorker(
  asset: EditorAsset,
  resolveSource: SourceResolver,
  fetchSource: SourceFetcher = fetch,
): Promise<EditorAsset> {
  if (asset.kind !== 'video' && asset.kind !== 'image' && asset.kind !== 'audio') return asset;

  const existingBlob = asset.metadata?.['blob'];
  if (typeof Blob !== 'undefined' && existingBlob instanceof Blob) return asset;

  // Prefer the in-session URL because it already represents the bytes visible in Media.
  // A serialized cross-device project may still contain a source-device blob URL, though;
  // if it is no longer readable, fall back to the durable R2/file key exactly once.
  const localUrl = (asset.objectUrl || '').trim();
  const durableUrl = [asset.fileKey, asset.sourceUrl]
    .map(value => (value || '').trim())
    .find(value => value && !isMemoryUrl(value)) || '';
  const sourceUrl = (asset.sourceUrl || '').trim();

  if (localUrl && isMemoryUrl(localUrl)) {
    try {
      return await withMemoryBlob(asset, localUrl, fetchSource);
    } catch {
      if (!durableUrl) throw unreadableMemoryError(asset);
    }
  }

  const rawUrl = durableUrl || sourceUrl || localUrl;
  if (!rawUrl) return asset;
  const resolved = isMemoryUrl(rawUrl) ? rawUrl : await resolveSource(rawUrl, asset.kind);
  if (!isMemoryUrl(resolved)) {
    return resolved && resolved !== sourceUrl ? { ...asset, sourceUrl: resolved } : asset;
  }

  try {
    return await withMemoryBlob(asset, resolved, fetchSource);
  } catch {
    throw unreadableMemoryError(asset);
  }
}

function isMemoryUrl(url: string): boolean {
  return url.startsWith('blob:') || url.startsWith('data:');
}

async function withMemoryBlob(
  asset: EditorAsset,
  url: string,
  fetchSource: SourceFetcher,
): Promise<EditorAsset> {
  const response = await fetchSource(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  if (!blob.size) throw new Error('Media is empty');
  return {
    ...asset,
    sourceUrl: url,
    metadata: { ...(asset.metadata ?? {}), blob },
  };
}

function unreadableMemoryError(asset: EditorAsset): Error {
  return new Error(`"${asset.name || 'Media'}" could not be read from memory. Remove it from Media, add it again, and retry export.`);
}
